import { ThemeProvider } from '@zennolab_platform/webui-kit/dist/ui';
import { lightTheme } from '@zennolab_platform/webui-kit/dist/ui/theme/light';
import { IntlProvider } from '@zennolab_platform/webui-sdk/dist/stores/IntlStore';
import { App as AntdApp } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Container } from 'typedi';

import { LocaleController } from '../../controllers/LocaleController';
import { SessionController } from '../../controllers/SessionController';
import { useMountInit } from '../../hooks/useMountInit';
import { IntlStore } from '../../stores/IntlStore';
import { SessionStore } from '../../stores/SessionStore';
import { isEmbedded } from '../../utils/embedding';
import { Checking, LoginScreen, NotConfigured } from '../screens';
import { Shell } from '../Shell';

export const App = observer(function App({ missingConfig = [] }: { missingConfig?: string[] }) {
  const intlStore = Container.get(IntlStore);
  const localeController = Container.get(LocaleController);
  const ready = useMountInit(localeController.init);
  const { locale } = intlStore;

  // Screen readers and the browser's spellcheck read the page language.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => (isEmbedded() ? localeController.followParent() : undefined), [localeController]);

  if (!ready) return null;
  return (
    // Always light: the app is framed by UA2, which is light; the kit would otherwise read a stored theme.
    <ThemeProvider theme={lightTheme}>
      <AntdApp component={false}>
        <IntlProvider intlService={intlStore}>
          <BrowserRouter>
            {missingConfig.length > 0 ? <NotConfigured names={missingConfig} /> : <SessionGate />}
          </BrowserRouter>
        </IntlProvider>
      </AntdApp>
    </ThemeProvider>
  );
});

const SessionGate = observer(function SessionGate() {
  const sessionStore = Container.get(SessionStore);
  const sessionController = Container.get(SessionController);

  useEffect(() => {
    void sessionController.check();
  }, [sessionController]);

  const { state } = sessionStore;
  if (state.status === 'checking') return <Checking />;
  if (state.status === 'anonymous') {
    return (
      <LoginScreen
        error={state.error}
        inFrame={sessionController.inFrame()}
        onLogin={sessionController.login}
        onRetry={sessionController.retry}
      />
    );
  }
  return <Shell />;
});
